/**
 * 平台适配层 — 统一抽象小程序 / APK / 浏览器容器
 *
 * 目标：H5 侧只调用 Platform 接口，不感知具体容器。
 * 容器能力见 web/README.md §5.3：
 *   - 微信小程序：`wx.miniProgram` + `wx.setStorageSync`
 *   - 支付宝小程序：`my.miniProgram` + `my.setStorage`
 *   - 浏览器 / Capacitor：`null` + `localStorage`
 */

export type PlatformName = 'weapp' | 'alipay' | 'web' | 'apk';

declare global {
  interface Window {
    wx?: any;
    my?: any;
  }
}

function detectPlatform(): PlatformName {
  if (typeof window === 'undefined') return 'web';
  const w = window as any;
  if (typeof w.wx !== 'undefined' && w.wx.miniProgram) return 'weapp';
  if (typeof w.my !== 'undefined' && w.my.miniProgram) return 'alipay';
  return 'web';
}

/** 简单的键值存取适配：小程序用同步 API，浏览器用 localStorage */
export const StorageAdapter = {
  getItem(key: string): string | null {
    const name = detectPlatform();
    try {
      if (name === 'weapp') {
        return (window as any).wx.getStorageSync(key) ?? null;
      }
      if (name === 'alipay') {
        const v = (window as any).my.getStorageSync({ key })?.data;
        return v !== undefined && v !== null ? String(v) : null;
      }
      return localStorage.getItem(key);
    } catch (e) {
      console.error(`[Storage] getItem(${key}) 失败:`, e);
      return null;
    }
  },

  setItem(key: string, value: string): void {
    const name = detectPlatform();
    try {
      if (name === 'weapp') {
        (window as any).wx.setStorageSync(key, value);
        return;
      }
      if (name === 'alipay') {
        (window as any).my.setStorageSync({ key, data: value });
        return;
      }
      localStorage.setItem(key, value);
    } catch (e) {
      console.error(`[Storage] setItem(${key}) 失败:`, e);
    }
  },

  removeItem(key: string): void {
    const name = detectPlatform();
    try {
      if (name === 'weapp') {
        (window as any).wx.removeStorageSync(key);
        return;
      }
      if (name === 'alipay') {
        (window as any).my.removeStorageSync({ key });
        return;
      }
      localStorage.removeItem(key);
    } catch (e) {
      console.error(`[Storage] removeItem(${key}) 失败:`, e);
    }
  },
};

export const Platform = {
  name: detectPlatform(),
  save: StorageAdapter.setItem.bind(StorageAdapter),
  load: StorageAdapter.getItem.bind(StorageAdapter),
  remove: StorageAdapter.removeItem.bind(StorageAdapter),
  /** 容器跳转（小程序内 navigateTo / 浏览器 history） */
  navigate(path: string): void {
    const name = Platform.name;
    if (name === 'weapp') (window as any).wx.miniProgram.navigateTo({ url: path });
    else if (name === 'alipay') (window as any).my.miniProgram.navigateTo({ url: path });
    else window.location.href = path;
  },
};
